import { Box, Divider, Grid } from '@mui/material';
import { useDialog } from 'app/context';
import { Button, InputDateTimePicker, InputSearchField, SearchDoctor, TextField, Typography } from 'app/shared';
import { formatDate } from 'app/utils';
import { IDoctorSchema, IReportSchema, IReportUpdate } from 'generated/axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { getReportStatus } from './utils';
import { FormHook } from 'app/hooks';

interface Props {
  detail: IReportSchema | undefined;
  updateForm: FormHook<IReportUpdate>;
  navigate: NavigateFunction;
  loading: boolean;
  getPatientSignature: () => void;
}

export const ReportDetailColumn = ({ detail, updateForm, loading, getPatientSignature }: Props) => {
  const { createDialog, closeDialog } = useDialog();
  const { setValue, bindTextField } = updateForm;

  const [selectedDoctor, setSelectedDoctor] = useState<IDoctorSchema | undefined | null>();

  //search doctor dialog
  const handleDoctorDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchDoctor
          onCancel={closeDialog}
          onConfirmDoctor={(doctor) => {
            setSelectedDoctor(doctor);
            closeDialog();
          }}
        />
      ),
    });

  //state update from async value
  useEffect(() => {
    setSelectedDoctor(detail?.doctor);
  }, [detail]);

  //updates request with selected doctor
  useEffect(() => {
    setValue({ field: 'doctor_id', value: selectedDoctor?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor]);

  return (
    <>
      <Divider sx={{ paddingBottom: 4 }}>
        <Typography variant="body2">{t('common.patient')}</Typography>
      </Divider>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField label={t('patient.lastName')} disabled value={detail?.patient?.last_name} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('patient.firstName')} disabled value={detail?.patient?.first_name} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('patient.birthDate')} disabled value={formatDate(detail?.patient?.birth_date)} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('patient.company')} disabled value={detail?.patient?.company?.name} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('patient.taxCode')} disabled value={detail?.patient?.tax_code} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('patient.job')} disabled value={detail?.patient?.job_title} />
        </Grid>
      </Grid>
      <Divider sx={{ padding: 4 }}>
        <Typography variant="body2">{t('common.doctor')}</Typography>
      </Divider>
      <Grid container spacing={2}>
        <Grid size={12}>
          <InputSearchField
            label={t('common.doctor')}
            fieldValue={(selectedDoctor?.first_name ?? '') + ' ' + (selectedDoctor?.last_name ?? '')}
            onSearch={handleDoctorDialog}
          />
        </Grid>
      </Grid>
      <Divider sx={{ padding: 4 }}>
        <Typography variant="body2">{t('common.report')}</Typography>
      </Divider>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField label={t('common.createdAt')} disabled value={formatDate(detail?.created_at)} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('common.updatedAt')} disabled value={formatDate(detail?.updated_at)} />
        </Grid>
        <Grid size={12}>
          <TextField label={t('common.status')} disabled value={getReportStatus(detail?.report_status)}></TextField>
        </Grid>
        <Grid size={12}>
          <Box
            padding={1}
            gap={1}
            sx={{ textAlign: 'start', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <label>
              {detail?.has_patient_signature ? t('report.hasPatientSignature') : t('report.noPatientSignature')}
            </label>
            {detail?.has_patient_signature && (
              <Button onClick={getPatientSignature} loading={loading}>
                {t('actions.download')}
              </Button>
            )}
          </Box>
        </Grid>
        <Grid size={6}>
          <TextField label={t('report.temperature')} {...bindTextField({ field: 'temperature' })} />
        </Grid>
        <Grid size={6}>
          <TextField label={t('report.urineQuantity')} {...bindTextField({ field: 'urine_quantity' })} />
        </Grid>
        <Grid size={12}>
          <InputDateTimePicker
            label={t('report.testExecutionDate')}
            defaultValue={detail?.test_execution_date}
            onDatePick={(value) => setValue({ field: 'test_execution_date', value: value })}
          />
        </Grid>
        <Grid size={12}>
          <InputDateTimePicker
            label={t('report.reportingDate')}
            defaultValue={detail?.report_date}
            onDatePick={(value) => setValue({ field: 'report_date', value: value })}
          />
        </Grid>
        <Grid size={12}>
          <TextField label={t('report.notes')} multiline rows={3} {...bindTextField({ field: 'comment' })} />
        </Grid>
      </Grid>
    </>
  );
};
